import React from 'react';
import { Stack, Button, Text, DataList, DataItem } from '@cad-arc';
import { useWorkspacePanel } from '../../../panels/hooks/use-workspace-panel';
import { EventPanelTab } from '../components/event-panel-tab';
import { TabBody } from '../../../panels/entity-panels/tabs/tab-body';
import { TabSection } from '../../../panels/entity-panels/tabs/tab-section';
import { formatHoursMinutesSeconds } from '../../../common/helpers/utils';
import { useDrawerControls } from '../../../drawers/components/drawer-context-provider';
import { AssignedSection } from './components/assigned-section';
import { useEventMetadataStore } from '../../event-panel-form/event-metadata';
import { globalAttributes } from '../../../common/global-attributes';
import { observer } from 'mobx-react-lite';
import { capitalizeEachWordInString } from '../../../common/helpers/typography';
import { useEventUnitsTabQuery } from '../../event-state/use-event-units-tab-query';

export const UnitsTab = observer(function UnitsTab(): React.ReactElement {
    const eventId = useWorkspacePanel().linkedEntityId;
    const eventMetadataStore = useEventMetadataStore();

    const drawerControls = useDrawerControls();

    const isFireEvent =
        eventMetadataStore.state.metadata?.agencyTypeGlobalAttrId ===
        globalAttributes.agencyTypeGlobal.FIRE;

    const { isFetching, data } = useEventUnitsTabQuery({ eventId });

    return (
        <EventPanelTab
            title="Units"
            titleTrailingNode={
                <Button
                    variant="ghost"
                    onClick={() => {
                        if (eventId) {
                            drawerControls.open({
                                type: 'manage-units',
                                props: {
                                    eventId,
                                },
                            });
                        }
                    }}
                >
                    Manage Units
                </Button>
            }
        >
            <TabBody>
                <AssignedSection />
                <TabSection
                    title="Recommendations"
                    emptyText="No unit recommendation generated for this event"
                    loading={isFetching}
                >
                    {!!data?.unitRecommendationsViews.length && (
                        <Stack spacing={4}>
                            {data?.unitRecommendationsViews.map((unitRecommendationView, idx) => (
                                <Stack key={idx} spacing={1}>
                                    <Stack direction="row" justifyContent="space-between">
                                        {!!unitRecommendationView.reason && (
                                            <Text fontSize="sm" fontWeight="bold">
                                                {
                                                    // Some light formatting b/c the BE
                                                    // value comes back as all caps
                                                    capitalizeEachWordInString(
                                                        unitRecommendationView.reason.toLowerCase()
                                                    )
                                                }
                                            </Text>
                                        )}
                                        <Stack direction="row">
                                            <Text
                                                variant="caption"
                                                fontFamily="mono"
                                                color="tertiary"
                                            >
                                                {[
                                                    formatHoursMinutesSeconds(
                                                        unitRecommendationView.createdDateUtc
                                                    ),
                                                    unitRecommendationView.createdByInitials,
                                                ]
                                                    .filter(Boolean)
                                                    .join(' ')}
                                            </Text>
                                        </Stack>
                                    </Stack>
                                    <DataList spacing={1}>
                                        <React.Fragment>
                                            {[
                                                {
                                                    // Adding this data point in because we have it,
                                                    // though I'm still unsure when / how this ever gets
                                                    // surfaced on the FE
                                                    label: 'Error',
                                                    value: unitRecommendationView.errorMessage,
                                                },
                                                {
                                                    label: 'Recommended Units',
                                                    value: unitRecommendationView.unitCallSigns,
                                                },
                                                {
                                                    label: 'Alarm Level',
                                                    value: isFireEvent
                                                        ? unitRecommendationView.alarmLevel
                                                        : undefined,
                                                },
                                                {
                                                    label: 'Resource Pattern',
                                                    value: unitRecommendationView.resourcePattern,
                                                },
                                                {
                                                    label: 'Resource Pattern Notes',
                                                    value:
                                                        unitRecommendationView.resourcePatternNotes,
                                                },
                                                {
                                                    label: 'Run Order',
                                                    value: unitRecommendationView.runOrder,
                                                },
                                                {
                                                    label: 'Move Ups',
                                                    value: unitRecommendationView.moveUps.length ? (
                                                        <Stack spacing={0}>
                                                            {unitRecommendationView.moveUps.map(
                                                                (moveUp, idx) => (
                                                                    <Text key={idx}>{moveUp}</Text>
                                                                )
                                                            )}
                                                        </Stack>
                                                    ) : undefined,
                                                },
                                                {
                                                    label: 'Move Up Notes',
                                                    value: unitRecommendationView.moveUpsNotes,
                                                },
                                                {
                                                    label: 'Notes',
                                                    value: unitRecommendationView.notes,
                                                },
                                            ]
                                                .flatMap(({ label, value }) => {
                                                    return value ? [{ label, value }] : [];
                                                })
                                                .map(({ label, value }, idx) => {
                                                    // We map over these data points so that we can filter
                                                    // out data points that are empty (in the previous `flatMap`)
                                                    return (
                                                        <DataItem key={idx} dataKey={label}>
                                                            {value}
                                                        </DataItem>
                                                    );
                                                })}
                                        </React.Fragment>
                                    </DataList>
                                </Stack>
                            ))}
                        </Stack>
                    )}
                </TabSection>
            </TabBody>
        </EventPanelTab>
    );
});
